import type {Context} from '../types';
import {
  createNewCMSBinding,
  isBoundToCMS,
  getDateType,
  deleteDynBindProperty,
  getExtendedBindingData,
  ExtendedBindingData,
} from '../utils/legacyBindings';
import {ERecord, Expression} from '@packages/systems/wfdl-ast';
import {ExpressionPath, Expressions} from '@packages/systems/wfdl';

const IMG_SRC_EXPR_PATH: ExpressionPath = [{in: 'Record', at: 'img'}];

const IMG_ALT_EXPR_PATH: ExpressionPath = [
  {in: 'Record', at: 'attr'},
  {in: 'Record', at: 'alt'},
];

export default function imageCMSBindingsToUB(context: Context) {
  const {jsonNode} = context.node;
  const imageSrcUB = getImageUniversalBinding(context);
  const imageAltUB = getImageAltTextUniversalBinding(context);
  const {data = {}} = jsonNode;

  const needed =
    (imageSrcUB !== null || imageAltUB !== null) &&
    (jsonNode.type === 'Image' || jsonNode.tag === 'img');
  return {
    needed,
    logData: {
      msg: 'Migrate Image legacy CMS bindings to Universal Bindings',
      meta: {
        nodeId: jsonNode._id,
      },
    },
    apply: () => {
      if (imageSrcUB === null && imageAltUB === null) {
        return;
      }

      if (imageSrcUB !== null) {
        deleteDynBindProperty(jsonNode, 'src');
        if (jsonNode.data) {
          // eslint-disable-next-line webflow/no-delete
          delete jsonNode.data.img;
        }
        jsonNode.bind = Expressions.sparseSetIn(
          jsonNode.bind || ERecord({}),
          IMG_SRC_EXPR_PATH,
          imageSrcUB
        );
      }
      if (imageAltUB !== null) {
        deleteDynBindProperty(jsonNode, 'alt');
        if (jsonNode.data) {
          // eslint-disable-next-line webflow/no-delete
          delete jsonNode.data.alt;
        }
        jsonNode.bind = Expressions.sparseSetIn(
          jsonNode.bind || ERecord({}),
          IMG_ALT_EXPR_PATH,
          imageAltUB
        );
      }
      deleteEmptyImgData(data);
    },
  };
}

const deleteEmptyImgData = (data: Record<string, string>) => {
  if (data.img && Object.keys(data.img).length === 0) {
    // eslint-disable-next-line webflow/no-delete
    delete data.img;
  }
};

function getImageAltTextUniversalBinding(context: Context): Expression | null {
  const {jsonNode} = context.node;
  const {data = {}} = jsonNode;
  if (jsonNode.type === 'Image' || jsonNode.tag === 'img') {
    const dynBind = data.dyn?.bind?.alt;
    const bindingData = dynBind ? getExtendedBindingData(dynBind) : false;
    if (bindingData && isBoundToCMS(context)) {
      return getUniversalBinding('text', context, bindingData);
    }
  }
  return null;
}

function getImageUniversalBinding(context: Context): Expression | null {
  const {jsonNode} = context.node;
  const {data = {}} = jsonNode;
  if (jsonNode.type === 'Image' || jsonNode.tag === 'img') {
    const dynBind = data.dyn?.bind?.src;
    const bindingData = dynBind ? getExtendedBindingData(dynBind) : false;
    if (bindingData && isBoundToCMS(context)) {
      return getUniversalBinding('image', context, bindingData);
    }
  }
  return null;
}

function getUniversalBinding(
  targetType: 'text' | 'image',
  context: Context,
  bindingData: ExtendedBindingData
): Expression | null {
  const bindingDataType =
    bindingData.type === 'Date'
      ? getDateType(context, bindingData)
      : bindingData.type;
  return createNewCMSBinding(
    bindingDataType,
    targetType,
    bindingData.slug,
    bindingData.filter
  );
}
